import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Container, Divider, Stack, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import CompanyLogo from "../../assets/svgIcons/company-logo.svg";
import TickIcon from "../../assets/icons/half_tick.png";
import FooterBanner from "../../assets/icons/footer_banner.png";
import consentBg from "../../assets/icons/consent_bg.png";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import { decryptObject } from "../../utils/crypto";

const consentItems = [
  ["KYC & Verification:", "For relying upon KYC documents supplied to us, whether by you or through our representative/service provider, for meeting our legal, regulatory and compliance obligations such as customer due diligence, know your customer/re-KYC/anti-money laundering checks, processing your loan application details and other value-added services"],
  ["Registry & Bureau Reporting:", "For providing your application details to Central KYC Registry, UIDAI, credit information companies/credit bureaus for verification purposes"],
  ["Educational Verification:", "For verifying the applied course or admission details, credentials, and enrollment with the relevant institute or third parties"],
  ["Repayment & Recovery:", "For enabling repayment and ensuring recovery of amounts due from you including through our appointed recovery agents, etc."],
  ["General Administration:", "For our general administration and granting services in relation to the above"],
  ["Communication Consent:", "For contacting you over Emails, WhatsApp, call and SMS, overriding NDNC registration."],
];

type OtpType = "mobile" | "email";
type FlowState = "confirmation" | "otp" | "success";
const COOLDOWN_SECONDS = 180;
const clean = (value: unknown) => String(value ?? "").trim();
const formatTimer = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

const ConsentAndAuthorizationPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const encryptedData = params.get("data") || "";
  const [session, setSession] = useState<any>(null);
  const [consentFlags, setConsentFlags] = useState({ isMobileVerified: false, isEmailVerified: false });
  const [consentLoading, setConsentLoading] = useState(true);
  const [flowState, setFlowState] = useState<FlowState>("confirmation");
  const [otpValues, setOtpValues] = useState({ mobile: "", email: "" });
  const [timers, setTimers] = useState({ mobile: 0, email: 0 });
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState<Record<OtpType, boolean>>({ mobile: false, email: false });
  const [error, setError] = useState("");
  const [isSessionTimedOut, setIsSessionTimedOut] = useState(false);
  const timerEnds = useRef<Partial<Record<OtpType, number>>>({});
  const initializedUrlState = useRef(false);

  const sessionExpired = isSessionTimedOut || Boolean(session?.expiresAt && new Date(session.expiresAt).getTime() <= Date.now());
  const required = useMemo(() => ({
    mobile: !consentFlags.isMobileVerified,
    email: !consentFlags.isEmailVerified,
  }), [consentFlags]);

  const updateUrlState = useCallback((state: FlowState) => {
    const next = new URLSearchParams(params);
    next.set("state", state);
    setParams(next, { replace: true });
  }, [params, setParams]);

  const startTimer = useCallback((type: OtpType) => {
    timerEnds.current[type] = performance.now() + COOLDOWN_SECONDS * 1000;
    setTimers((current) => ({ ...current, [type]: COOLDOWN_SECONDS }));
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const now = performance.now();
      setTimers((current) => {
        const next = { ...current };
        (Object.keys(timerEnds.current) as OtpType[]).forEach((type) => {
          const end = timerEnds.current[type];
          next[type] = end ? Math.max(0, Math.ceil((end - now) / 1000)) : 0;
          if (next[type] === 0) delete timerEnds.current[type];
        });
        return next;
      });
    }, 250);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!encryptedData) {
      setError("Invalid consent link.");
      setConsentLoading(false);
      return;
    }
    try {
      const decoded = decryptObject(encryptedData);
      setSession(typeof decoded === "string" ? JSON.parse(decoded) : decoded);
    } catch {
      setError("Invalid consent link.");
      setConsentLoading(false);
    }
  }, [encryptedData]);

  useEffect(() => {
    if (!session?.expiresAt) return;
    const remaining = new Date(session.expiresAt).getTime() - Date.now();
    if (remaining <= 0) {
      setIsSessionTimedOut(true);
      timerEnds.current = {};
      setTimers({ mobile: 0, email: 0 });
      return;
    }
    const timeout = window.setTimeout(() => {
      setIsSessionTimedOut(true);
      timerEnds.current = {};
      setTimers({ mobile: 0, email: 0 });
      setError("Session has timed out");
    }, remaining);
    return () => window.clearTimeout(timeout);
  }, [session]);

  useEffect(() => {
    if (!encryptedData || sessionExpired) {
      if (sessionExpired) setError("Session has timed out");
      setConsentLoading(false);
      return;
    }
    let active = true;
    const checkConsent = async () => {
      setConsentLoading(true);
      try {
        const response: any = await eligibilityCheckServices.checkConsent(encryptedData);
        const result = response?.data?.data || response?.data || {};
        if (active) setConsentFlags({ isMobileVerified: Boolean(result.isMobileVerified), isEmailVerified: Boolean(result.isEmailVerified) });
      } catch (e: any) {
        if (active) setError(e?.response?.data?.message || "Unable to check consent status.");
      } finally {
        if (active) setConsentLoading(false);
      }
    };
    checkConsent();
    return () => { active = false; };
  }, [encryptedData, sessionExpired]);

  useEffect(() => {
    const state = params.get("state");
    if (!initializedUrlState.current) {
      initializedUrlState.current = true;
      if (state === "otp") {
        const next = new URLSearchParams(params);
        next.delete("state");
        setParams(next, { replace: true });
        setFlowState("confirmation");
        return;
      }
    }
    setFlowState(
      state === "otp" || state === "success" ? state : "confirmation"
    );
  }, [params]);

  const sendOtp = async () => {
    if (sessionExpired || !session || !encryptedData) return setError(sessionExpired ? "Session has timed out" : "Invalid consent link.");
    setLoading(true); setError("");
    try {
      const response: any = await eligibilityCheckServices.sendConsentOtp(encryptedData);
      const result = response?.data?.data || {};
      const nextFlags = {
        isMobileVerified:
          consentFlags.isMobileVerified || Boolean(result?.mobile?.isAlreadyVerified),
        isEmailVerified:
          consentFlags.isEmailVerified || Boolean(result?.email?.isAlreadyVerified),
      };
      setConsentFlags(nextFlags);
      if (!nextFlags.isMobileVerified) startTimer("mobile");
      if (!nextFlags.isEmailVerified) startTimer("email");
      updateUrlState("otp");
      setFlowState("otp");
    } catch (e: any) {
      setError(e?.response?.data?.message || "Unable to send OTP.");
    } finally { setLoading(false); }
  };

  const resendOtp = async (type: OtpType) => {
    if (timers[type] > 0 || resending[type] || sessionExpired || !session) return;
    setResending((current) => ({ ...current, [type]: true }));
    setError("");
    const channel = type === 'mobile' ? 'sms' : 'email';
    try {
      await eligibilityCheckServices.resendConsentOtp(encryptedData, channel);
      startTimer(type);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Unable to resend OTP.");
    } finally { setResending((current) => ({ ...current, [type]: false })); }
  };

  const verifyOtp = async () => {
    if (sessionExpired || !session) {
      setError("Session has timed out");
      return;
    }
    const payload: { data: string; mobileOtp?: string; emailOtp?: string } = { data: encryptedData };
    if (required.mobile) payload.mobileOtp = otpValues.mobile;
    if (required.email) payload.emailOtp = otpValues.email;
    if ((required.mobile && payload.mobileOtp?.length !== 6) || (required.email && payload.emailOtp?.length !== 6)) {
      setError("Please enter the complete OTP.");
      return;
    }
    setLoading(true); setError("");
    try {
      await eligibilityCheckServices.verifyConsentOtp(payload);
      updateUrlState("success");
      setFlowState("success");
    } catch (e: any) {
      setError(e?.response?.data?.message || "OTP verification failed.");
    } finally { setLoading(false); }
  };

  const alreadyConsented = consentFlags.isMobileVerified && consentFlags.isEmailVerified;
  const name = clean(session?.userType || "");

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fff", backgroundImage: `url(${consentBg})`, backgroundSize: "100% auto", backgroundRepeat: "no-repeat" }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 5, sm: 7 }, px: { xs: 1.5, sm: 3 } }}>
        <Box sx={{ bgcolor: "#fff", borderRadius: "8px", boxShadow: "0 4px 20px rgba(0,0,0,.09)", overflow: "hidden" }}>
          <Box sx={{ px: { xs: 2.5, sm: 4 }, pt: 2.5, pb: 2 }}><img src={CompanyLogo} alt="Company Logo" style={{ height: isMobile ? 32 : 40 }} /></Box>
          <Divider />
          {error && <Alert severity="error" sx={{ mx: { xs: 2.5, sm: 4 }, mt: 2 }}>{error}</Alert>}
          {consentLoading ? <Box sx={{ minHeight: 350, display: "grid", placeItems: "center" }}><CircularProgress sx={{ color: "#00B3B5" }} /></Box> : sessionExpired ? <SessionExpiredView onClose={() => navigate(-1)} /> : flowState === "success" ? <SuccessView onClose={() => navigate(-1)} /> : flowState === "otp" ? <OtpView required={required} values={otpValues} setValues={setOtpValues} timers={timers} resending={resending} onResend={resendOtp} onVerify={verifyOtp} loading={loading} sessionExpired={Boolean(sessionExpired)} /> : <ConfirmationView name={name} alreadyConsented={alreadyConsented} onClose={() => navigate(-1)} onProceed={sendOtp} loading={loading} />}
        </Box>
      </Container>
      <img src={FooterBanner} alt="footer banner" style={{ width: "100%", marginTop: 8 }} />
    </Box>
  );
};

const ConfirmationView = ({ name, alreadyConsented, onClose, onProceed, loading }: any) => <Box sx={{ px: { xs: 2.5, sm: 4 }, pt: 2.5, pb: 4 }}>
  <Typography sx={{ fontWeight: 700, fontSize: { xs: ".95rem", sm: "1.05rem" }, mb: 1.2, color: "#111" }}>Consent and Authorization</Typography>
  {alreadyConsented ? <Typography sx={{ fontWeight: 600, color: "#00B3B5", py: 2 }}>Thank you. We have already recorded your consent.</Typography> : <>
    <Typography sx={{ fontSize: { xs: ".77rem", sm: ".96rem" }, color: "#333", mb: 2.5, lineHeight: 1.65 }}>By verifying the OTP, you expressly consent to <Box component="span" sx={{ color: "#00B3B5", fontWeight: 600 }}>Avanse Financial Services Ltd ("AFSL")</Box>, its authorized representative/ third parties, regulators, or governmental authorities to collect and process your data as outlined below.</Typography>
    <Stack spacing={1.8}>{consentItems.map(([label, description]) => <Box key={label} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}><img src={TickIcon} alt="tick" style={{ width: 20 }} /><Typography sx={{ fontSize: { xs: ".76rem", sm: ".96rem" }, color: "#333", lineHeight: 1.65 }}><Box component="span" sx={{ fontWeight: 700, color: "#111" }}>{label}</Box> {description}</Typography></Box>)}</Stack>
    <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3, flexDirection: { xs: "column-reverse", sm: "row" } }}><Button variant="outlined" onClick={onClose} sx={secondarySx}>Close</Button><Button variant="contained" onClick={onProceed} disabled={loading} sx={primarySx}>{loading ? <CircularProgress size={18} color="inherit" /> : "Proceed with OTP Verification"}</Button></Box>
  </>}
</Box>;

const OtpView = ({ required, values, setValues, timers, resending, onResend, onVerify, loading, sessionExpired }: any) => <Box sx={{ px: { xs: 2.5, sm: 4 }, py: 5 }}>
  <Typography sx={{ textAlign: "center", fontWeight: 700, fontSize: { xs: "1rem", sm: "1.35rem" }, mb: 3 }}>Please enter the OTP{required.mobile && required.email ? "s" : ""} sent to your {required.mobile && required.email ? "mobile number and email ID" : required.mobile ? "mobile number" : "email ID"}.</Typography>
  <Stack spacing={2} sx={{ maxWidth: 470, mx: "auto" }}>{(["mobile", "email"] as OtpType[]).filter((type) => required[type]).map((type) => <OtpCard key={type} type={type} value={values[type]} timer={timers[type]} isResending={resending[type]} onChange={(value: string) => setValues((current: any) => ({ ...current, [type]: value }))} onResend={() => onResend(type)} />)}</Stack>
  <Button variant="contained" onClick={onVerify} disabled={loading || sessionExpired} sx={{ ...primarySx, display: "block", mx: "auto", mt: 3 }}>{loading ? <CircularProgress size={18} color="inherit" /> : "Confirm & Verify"}</Button>
</Box>;

const OtpCard = ({ type, value, timer, isResending, onChange, onResend }: any) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const handleInputChange = (index: number, inputValue: string) => {
    const digit = inputValue.replace(/\D/g, "").slice(-1);
    onChange((value.slice(0, index) + digit + value.slice(index + 1)).slice(0, 6));
    if (digit && index < 5) inputRefs.current[index + 1]?.focus();
  };

  return <Box sx={{ p: 2, bgcolor: "#EAF8F9", borderRadius: "7px" }}>
  <Box sx={{ display: "flex", justifyContent: "space-between" }}><Typography fontSize={12}>Enter {type === "mobile" ? "Mobile" : "Email"} OTP<span style={{ color: "#ff4c51" }}>*</span></Typography><Typography fontSize={12}>{timer ? formatTimer(timer) : "Ready to resend"}</Typography></Box>
  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 1, mt: 1 }}>{Array.from({ length: 6 }).map((_, index) => <TextField key={index} value={value[index] || ""} inputRef={(element) => { inputRefs.current[index] = element; }} onChange={(event) => handleInputChange(index, event.target.value)} inputProps={{ maxLength: 1, inputMode: "numeric" }} sx={{ bgcolor: "#fff", "& input": { textAlign: "center" } }} />)}</Box>
  <Button onClick={onResend} disabled={timer > 0 || isResending} sx={{ color: "#00B3B5", fontWeight: 700, textTransform: "none", float: "right", mt: 1 }}>{isResending ? "Sending..." : "Resend OTP"}</Button>
</Box>;
};

const SuccessView = ({ onClose }: { onClose: () => void }) => <Box sx={{ minHeight: 350, display: "flex", alignItems: "center", flexDirection: "column", justifyContent: "center", gap: 2 }}><Typography sx={{ color: "#00B3B5", fontSize: 64 }}>✓</Typography><Typography sx={{ fontWeight: 700, fontSize: 24 }}>Verification Successful</Typography><Button variant="outlined" onClick={onClose} sx={secondarySx}>Close</Button></Box>;
const SessionExpiredView = ({ onClose }: { onClose: () => void }) => <Box sx={{ minHeight: 350, display: "flex", alignItems: "center", flexDirection: "column", justifyContent: "center", gap: 2, px: 2, textAlign: "center" }}><Typography sx={{ fontWeight: 700, fontSize: 24 }}>Session Timed Out</Typography><Typography sx={{ color: "#4B465C" }}>This consent session has expired. Please request a new verification link.</Typography><Button variant="outlined" onClick={onClose} sx={secondarySx}>Close</Button></Box>;
const primarySx = { bgcolor: "#00B3B5", textTransform: "none", fontWeight: 700, minHeight: 45, borderRadius: "6px", "&:hover": { bgcolor: "#00B3B5" } };
const secondarySx = { color: "#00B3B5", borderColor: "#00B3B5", textTransform: "none", fontWeight: 700, minHeight: 45, minWidth: 212, borderRadius: "6px" };

export default ConsentAndAuthorizationPage;
